import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import type { SelectChangeEvent } from '@mui/material';
import { Box, IconButton, MenuItem, Select, Typography } from '@mui/material';
import useCharger from 'hooks/useCharger';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useEffect, useState } from 'react';
import type { FC } from 'react';

import ScrollContainerWrapper from 'components/DeviceConfigComponents/ScrollContainerWrapper';
import CustomFormControl from 'components/MuiCustom/CustomFormControl';
import CustomSwitch from 'components/MuiCustom/CustomSwitch';
import CustomSwitchItem from 'components/MuiCustom/CustomSwitchItem';
import FilledTextField from 'components/MuiCustom/FilledTextField';

interface SetupOCPPProps {
  title?: string;
  description?: string;
  serial?: string;
  ocppUrl: string;
  appendSerialNumber: boolean;
  setOcppUrl: (value: string) => void;
  setAppendSerialNumber: (value: boolean) => void;
  handleSubmitOcppSetup?: (event: React.FormEvent<HTMLFormElement>) => void;
}

type Protocol = 'wss://' | 'ws://';

// Helper function for adding trailing slashes
const formatPathSlashes = (rawPath: string): string => {
  let cleanPath = rawPath.trim();
  if (!cleanPath) return '/';

  if (!cleanPath.startsWith('/')) {
    cleanPath = '/' + cleanPath;
  }

  if (!cleanPath.endsWith('/')) {
    cleanPath = cleanPath + '/';
  }

  return cleanPath;
};

const SetupOCPP: FC<SetupOCPPProps> = props => {
  const {
    title,
    description,
    serial,
    ocppUrl,
    appendSerialNumber,
    setOcppUrl,
    setAppendSerialNumber,
  } = props;

  const { t } = useTranslation();

  const initialOcppUrl = useCharger(serial, state => state?.ocppu || '');

  const [ocppUsername, setOcppUsername] = useState<string>('');
  const [ocppPassword, setOcppPassword] = useState<string>('');
  const [host, setHost] = useState<string>('');
  const [path, setPath] = useState<string>('');
  const [appendSerial, setAppendSerial] = useState<boolean>(
    serial ? false : appendSerialNumber,
  );

  const [protocol, setProtocol] = useState<Protocol>('wss://'); // default wss

  const handleProtocolChange = (event: SelectChangeEvent<Protocol>): void => {
    setProtocol(event.target.value as Protocol);
  };

  const parseURL = useCallback(
    (urlString: string) => {
      if (!urlString.trim()) {
        setProtocol('wss://');
        setOcppUsername('');
        setOcppPassword('');
        setHost('');
        setPath('');
        setAppendSerial(false);

        return;
      }

      try {
        const parsedURL = new URL(urlString);
        setProtocol(parsedURL.protocol.startsWith('wss') ? 'wss://' : 'ws://');
        setOcppUsername(parsedURL.username);
        setOcppPassword(parsedURL.password);
        setHost(parsedURL.host);

        let extractedPath = parsedURL.pathname;
        // Strip serial if it's already at the end to prevent duplicates
        if (serial && extractedPath.endsWith(serial)) {
          extractedPath = extractedPath.slice(0, -serial.length);
        }

        setPath(formatPathSlashes(extractedPath));
        setAppendSerial(
          serial ? parsedURL.pathname.endsWith(serial) : appendSerialNumber,
        );
      } catch {
        setHost('');

        return;
      }
    },
    [appendSerialNumber, serial],
  );

  // Parse initial URL on mount safely
  useEffect(() => {
    const fallbackUrl = ocppUrl ? ocppUrl : initialOcppUrl;
    parseURL(fallbackUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Construct URL from individual states
  useEffect(() => {
    if (!host) return;

    const userInfo =
      ocppUsername || ocppPassword ? `${ocppUsername}:${ocppPassword}@` : '';

    try {
      // Ensure the path is formatted before appending anything else
      const formattedPath = formatPathSlashes(path);
      const baseUrl = `${protocol}${userInfo}${host}${formattedPath}`;
      const url = serial && appendSerial ? `${baseUrl}${serial}` : baseUrl;

      setOcppUrl(url);
    } catch (error) {
      console.error('Failed to update URL:', error);
    }
  }, [
    protocol,
    ocppUsername,
    ocppPassword,
    host,
    path,
    setOcppUrl,
    appendSerial,
    serial,
  ]);

  const pasteURL = async (): Promise<void> => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      setOcppUrl(clipboardText);
      parseURL(clipboardText);
    } catch (err) {
      console.error('Failed to read clipboard text: ', err);
    }
  };

  return (
    <ScrollContainerWrapper height="60vh">
      <Box mb={4}>
        <Typography align="center" variant="h2" sx={{ mb: 2 }}>
          {title ? title : t('common:pages.device_setup.ocpp_setup.title')}
        </Typography>
        <Typography align="center" variant="body1">
          {description
            ? description
            : t('common:pages.device_setup.ocpp_setup.description')}
        </Typography>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        overflow="auto"
        flexGrow={0}
        flexShrink={1}
        flexBasis="auto"
        width="100%"
      >
        <Box mb={2} mt={2}>
          <FilledTextField
            label={t('common:pages.device_setup.ocpp_setup.title')}
            name="ocppUrl"
            type="text"
            value={ocppUrl}
            onChange={e => setOcppUrl(e.target.value)}
            onBlur={() => parseURL(ocppUrl)}
            helperText={t(
              'common:pages.device_setup.ocpp_setup.ocpp_url_structure',
            )}
            fullWidth
            InputProps={{
              endAdornment: (
                <IconButton sx={{ mr: -1 }} onClick={pasteURL}>
                  <ContentPasteIcon color="primary" />
                </IconButton>
              ),
            }}
          />
        </Box>
        <Box mb={2}>
          <CustomFormControl fullWidth>
            <Select
              id="protocol"
              value={protocol}
              onChange={handleProtocolChange}
              sx={{
                '& .MuiSelect-select': {
                  pt: 2,
                  pb: 2,
                },
              }}
              inputProps={{
                'aria-label': t(
                  'common:pages.device_setup.ocpp_setup.protocol',
                ),
              }}
              renderValue={selected => (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                >
                  <Typography variant="body1">
                    {t('common:pages.device_setup.ocpp_setup.protocol')}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {selected.replace('://', '').toUpperCase()}
                  </Typography>
                </Box>
              )}
              IconComponent={prop => (
                <UnfoldMoreRoundedIcon {...prop} fontSize="small" />
              )}
            >
              <MenuItem value="wss://">WSS</MenuItem>
              <MenuItem value="ws://">WS</MenuItem>
            </Select>
          </CustomFormControl>
        </Box>
        <Box mb={2}>
          <FilledTextField
            label={t('common:pages.device_setup.ocpp_setup.username')}
            name="username"
            type="text"
            value={ocppUsername}
            onChange={e => setOcppUsername(e.target.value)}
            fullWidth
          />
        </Box>
        <Box mb={2}>
          <FilledTextField
            label={t('common:pages.device_setup.ocpp_setup.password')}
            name="password"
            type="text"
            value={ocppPassword}
            onChange={e => setOcppPassword(e.target.value)}
            fullWidth
          />
        </Box>
        <Box mb={2}>
          <FilledTextField
            label={t('common:pages.device_setup.ocpp_setup.host')}
            name="host"
            type="text"
            value={host}
            onChange={e => setHost(e.target.value)}
            fullWidth
          />
        </Box>
        <Box mb={2}>
          <FilledTextField
            label={t('common:pages.device_setup.ocpp_setup.path')}
            name="path"
            type="text"
            value={path}
            onChange={e => setPath(e.target.value)}
            onBlur={() => setPath(formatPathSlashes(path))}
            fullWidth
          />
        </Box>
        <Box mb={2}>
          <CustomSwitchItem isSingleItem>
            <Typography align="center" variant="body1">
              {t('common:pages.device_setup.ocpp_setup.append_sn_url')}
            </Typography>
            <CustomSwitch
              checked={appendSerial}
              onChange={() => {
                const nextValue = !appendSerial;
                setAppendSerial(nextValue);
                setAppendSerialNumber(nextValue);
              }}
            />
          </CustomSwitchItem>
        </Box>
      </Box>
    </ScrollContainerWrapper>
  );
};

export default SetupOCPP;
